import { forwardRef, useRef, useEffect } from 'react';
import type { FunctionComponent, PropsWithoutRef, Ref } from 'react';
import styled from 'styled-components';

import type { ForwardProps } from '../../../types';
import FormField from '../../FormField';
import Select, { Option } from '../../Select';
import type DateTimeProps from '../DateTime.types';
import type { DateTimeCallbackParameter } from '../DateTime.types';
import Flex from '../../Flex';
import { createClassName, range, withTestIds } from '../../../utils';
import {
  useConfiguration,
  useConsolidatedRef,
  useFocusWithin,
  useI18n,
  useTestIds
} from '../../../hooks';
import { StyledSelect } from '../../Select/Select';
import { defaultThemeProp } from '../../../theme';
import { StyledFormControl } from '../../FormControl';
import type { FormControlProps } from '../../FormControl';
import { getQuarterInputTestIds } from '../DateTime.test-ids';
import { StyledFormField } from '../../FormField/FormField';

import { getQuarter, parseToDate } from './utils';
import type { QuarterNumber } from './utils';
import StyledDateTime from './DateTime.styles';
import type { DateParts } from './DateTimeInput.types';

export interface QuarterInputProps extends DateTimeProps {
  /** Pass a heading and content to show additional information on the field. */
  additionalInfo?: FormControlProps['additionalInfo'];
}

export const StyledQuarterInput = styled(StyledDateTime)<QuarterInputProps>`
  padding: 0;
  border: 0;

  ${StyledFormField}:enabled > &:focus-within {
    box-shadow: none;
  }

  ${StyledSelect} {
    min-width: 0;

    &:focus {
      z-index: 1;
    }

    &:first-child {
      border-start-end-radius: 0;
      border-end-end-radius: 0;
    }

    &:last-child {
      max-width: max-content;
      margin-inline-start: -0.0625rem;
      border-start-start-radius: 0;
      border-end-start-radius: 0;
    }
  }
`;

StyledQuarterInput.defaultProps = defaultThemeProp;

const convertToCallbackParameter = (
  date: Date,
  parts: DateParts<string | undefined>
): DateTimeCallbackParameter => {
  if ([parts.quarter, parts.year].every(Boolean)) {
    const quarterStartMonth = [0, 3, 6, 9][Number(parts.quarter) - 1];

    const resultDate = new Date(date);
    resultDate.setUTCFullYear(Number(parts.year), quarterStartMonth, 1);
    return {
      valueAsISOString: resultDate.toISOString(),
      valueAsTimestamp: resultDate.getTime()
    };
  }
  if ([parts.year, parts.quarter].every(p => !p)) {
    return {
      valueAsISOString: undefined,
      valueAsTimestamp: undefined
    };
  }
  return {
    valueAsISOString: '',
    valueAsTimestamp: NaN,
    state: 'incomplete'
  };
};

const QuarterInput: FunctionComponent<QuarterInputProps & ForwardProps> = forwardRef(
  function QuarterInput(props: PropsWithoutRef<QuarterInputProps>, ref: Ref<HTMLFieldSetElement>) {
    const {
      testId,
      value,
      min,
      max,
      id,
      label,
      labelHidden,
      info,
      status,
      required,
      readOnly,
      disabled,
      onChange,
      onFocus,
      onBlur,
      autoFocus,
      additionalInfo,
      className,
      ...restProps
    } = props;

    const testIds = useTestIds(testId, getQuarterInputTestIds);

    const { locale } = useConfiguration();

    const date = value ? parseToDate(value) : undefined;
    const currentYear = date ? date.getUTCFullYear() : new Date().getFullYear();

    const minYear = min ? parseToDate(min).getUTCFullYear() : currentYear - 10;
    const maxYear = max ? parseToDate(max).getUTCFullYear() : currentYear + 10;

    const years = range(minYear, maxYear);

    const quarterSelectRef = useRef<HTMLSelectElement>(null);
    const yearSelectRef = useRef<HTMLSelectElement>(null);

    useEffect(() => {
      if (quarterSelectRef.current && yearSelectRef.current) {
        quarterSelectRef.current.value = date ? getQuarter(date).toString() : '';
        yearSelectRef.current.value = date?.getUTCFullYear().toString() ?? '';
      }
    }, [date?.toDateString()]);

    const pickParts = (): DateParts<string | undefined> => {
      return {
        quarter: quarterSelectRef.current?.value || '',
        year: yearSelectRef.current?.value || ''
      };
    };

    const onFocusChange = (focused: boolean) => {
      const callbackParam = convertToCallbackParameter(
        date ? new Date(date) : new Date(),
        pickParts()
      );
      if (onFocus && focused) onFocus(callbackParam);
      else if (onBlur && !focused) onBlur(callbackParam);
    };

    const onSelectChange = () => {
      onChange?.(convertToCallbackParameter(date ? new Date(date) : new Date(), pickParts()));
    };

    const containerRef = useConsolidatedRef<HTMLElement>(ref);
    useFocusWithin([containerRef], onFocusChange);

    const t = useI18n();

    const quarters: QuarterNumber[] = [1, 2, 3, 4];

    const quarterOptions = quarters.map(quarterNumber => (
      <Option key={t(`date_quarter_q${quarterNumber}`).trim()} value={quarterNumber.toString()}>
        {t(`date_quarter_q${quarterNumber}`).trim()}
      </Option>
    ));

    const yearOptions = years.map(year => (
      <Option key={year.toString()} value={year.toString()}>
        {year.toString()}
      </Option>
    ));

    const displayNames = new Intl.DisplayNames(locale, { style: 'long', type: 'dateTimeField' });

    const Quarter = (
      <Select
        data-testid={testIds.controlQuarter}
        key='quarter'
        ref={quarterSelectRef}
        aria-label={displayNames.of('quarter')}
        readOnly={readOnly}
        required={required}
        onChange={onSelectChange}
        status={status}
        disabled={disabled}
        autoFocus={autoFocus}
      >
        {!required && <Option key='null'> </Option>}
        {quarterOptions}
      </Select>
    );

    const Year = (
      <Select
        data-testid={testIds.controlYear}
        key='year'
        ref={yearSelectRef}
        aria-label={displayNames.of('year')}
        readOnly={readOnly}
        required={required}
        onChange={onSelectChange}
        status={status}
        disabled={disabled}
      >
        {!required && <Option key='null'> </Option>}
        {yearOptions}
      </Select>
    );

    const Comp = (
      <Flex
        data-testid={label ? undefined : testIds.root}
        {...restProps}
        as={StyledQuarterInput}
        className={createClassName('quarter-input', className)}
        forwardedAs={StyledFormControl}
        container={{ alignItems: 'center' }}
        ref={containerRef}
        status={status}
        disabled={disabled}
        readOnly={readOnly}
      >
        {Quarter}
        {Year}
      </Flex>
    );

    return label ? (
      <FormField
        testId={testIds}
        as='fieldset'
        label={label}
        labelHidden={labelHidden}
        id={id}
        info={info}
        readOnly={readOnly}
        status={status}
        required={required}
        disabled={disabled}
        additionalInfo={additionalInfo}
      >
        {Comp}
      </FormField>
    ) : (
      Comp
    );
  }
);

export default withTestIds(QuarterInput, getQuarterInputTestIds);
